import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, ArrowUp, ArrowDown, Trash2, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";

const LISTS = [
  { key: "dating_intentions", label: "Dating Intentions" },
  { key: "date_activities", label: "Ideal Date Activities" },
  { key: "interests", label: "Interests" },
  { key: "hobbies", label: "Hobbies" },
];
const btn = "rounded-full px-3 py-1 text-xs border transition-colors";

function ListEditor({ listKey, label, items, reload }) {
  const [adding, setAdding] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const handle = async (fn, okMsg) => {
    try { const { data } = await fn(); reload(data.options); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const add = () => {
    const v = adding.trim();
    if (!v) return;
    handle(() => api.post(`/admin/profile-options/${listKey}`, { label: v, active: true }), "Option added");
    setAdding("");
  };
  const saveEdit = (id) => { handle(() => api.put(`/admin/profile-options/${listKey}/${id}`, { label: editVal.trim() }), "Updated"); setEditId(null); };
  const toggle = (o) => handle(() => api.put(`/admin/profile-options/${listKey}/${o.id}`, { active: !o.active }), o.active ? "Disabled" : "Enabled");
  const disableFromConfirm = async (o) => { await handle(() => api.put(`/admin/profile-options/${listKey}/${o.id}`, { active: false }), "Disabled"); setConfirmDel(null); };
  const deleteFromConfirm = async (o) => { await handle(() => api.delete(`/admin/profile-options/${listKey}/${o.id}`), "Deleted"); setConfirmDel(null); };
  const move = (idx, dir) => {
    const arr = items.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    handle(() => api.post(`/admin/profile-options/${listKey}/reorder`, { order: arr }), null);
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-5" data-testid={`admin-optlist-${listKey}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-[#FBF8F3]">{label}</h3>
        <span className="text-[11px] text-[#9E978E]">{items.filter((i) => i.active).length} active · {items.length} total</span>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <input data-testid={`admin-optadd-input-${listKey}`} value={adding} onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New option label…" maxLength={50}
          className="flex-1 rounded-lg bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3] outline-none focus:border-[#D4AF37]/60" />
        <button data-testid={`admin-optadd-btn-${listKey}`} onClick={add} className={`${btn} border-[#D4AF37]/50 text-[#D4AF37] inline-flex items-center gap-1`}><Plus size={13} /> Add</button>
      </div>
      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        {items.map((o, idx) => (
          <div key={o.id} data-testid={`admin-opt-${listKey}-${o.id}`} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${o.active ? "border-white/10 bg-[#0C0A0A]/40" : "border-white/5 bg-[#0C0A0A]/20 opacity-60"}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} className="text-[#9E978E] hover:text-[#D4AF37] disabled:opacity-20" disabled={idx === 0}><ArrowUp size={12} /></button>
              <button onClick={() => move(idx, 1)} className="text-[#9E978E] hover:text-[#D4AF37] disabled:opacity-20" disabled={idx === items.length - 1}><ArrowDown size={12} /></button>
            </div>
            {editId === o.id ? (
              <input value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(o.id)}
                className="flex-1 rounded bg-[#0C0A0A]/60 border border-[#D4AF37]/40 px-2 py-1 text-sm text-[#FBF8F3] outline-none" autoFocus />
            ) : (
              <span className="flex-1 text-sm text-[#D8D2C9]">{o.label}{!o.active && <span className="text-[10px] text-[#9E978E] ml-2">(disabled)</span>}</span>
            )}
            {editId === o.id ? (
              <>
                <button onClick={() => saveEdit(o.id)} className="text-[#6FD79E] hover:text-[#8FE7B4]"><Check size={15} /></button>
                <button onClick={() => setEditId(null)} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={15} /></button>
              </>
            ) : (
              <>
                <button data-testid={`admin-optedit-${listKey}-${o.id}`} onClick={() => { setEditId(o.id); setEditVal(o.label); }} className="text-[#C9A96E] hover:text-[#D4AF37]" title="Edit"><Pencil size={14} /></button>
                <button data-testid={`admin-opttoggle-${listKey}-${o.id}`} onClick={() => toggle(o)} className="text-[#E0B84A] hover:text-[#F3D98B]" title={o.active ? "Disable" : "Enable"}>{o.active ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button data-testid={`admin-optdel-${listKey}-${o.id}`} onClick={() => setConfirmDel(o)} className="text-[#E58CA0] hover:text-[#8B263E]" title="Delete (only if unused)"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70" data-testid={`admin-optdel-confirm-${listKey}`}>
          <div className="w-full max-w-md rounded-2xl border border-[#8B263E]/40 bg-[#141113] p-6">
            <h3 className="font-serif text-lg text-[#FBF8F3] mb-2">Delete "{confirmDel.label}"?</h3>
            <p className="text-sm text-[#D8D2C9]">Permanent deletion is only allowed if no member currently uses this option. If it's in use, <span className="text-[#D4AF37]">disable</span> it instead — existing profiles keep their selection and it simply stops appearing as a new choice.</p>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <button data-testid={`admin-optdel-disable-${listKey}`} onClick={() => disableFromConfirm(confirmDel)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-[#541224] border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37]"><EyeOff size={14} /> Disable instead</button>
              <button data-testid={`admin-optdel-confirm-btn-${listKey}`} onClick={() => deleteFromConfirm(confirmDel)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-[#8B263E] text-white hover:bg-[#751A32]"><Trash2 size={14} /> Delete permanently</button>
              <button onClick={() => setConfirmDel(null)} className="ml-auto text-sm text-[#9E978E] hover:text-[#FBF8F3]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminProfileOptions() {
  const [data, setData] = useState(null);
  const load = async () => { try { const r = await api.get("/admin/profile-options"); setData(r.data); } catch (e) { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-[#9E978E] text-sm">Loading…</div>;
  return (
    <div className="space-y-5">
      <p className="text-sm text-[#9E978E]">Manage the selectable options members see. Disabling keeps existing member selections intact; deleting is only allowed when no member uses that option. Use the arrows to reorder.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        {LISTS.map((l) => (
          <ListEditor key={l.key} listKey={l.key} label={l.label} items={data[l.key] || []}
            reload={(opts) => setData((d) => ({ ...d, [l.key]: opts }))} />
        ))}
      </div>
    </div>
  );
}
